import { useEffect, useState } from "react";
import { getPosts, createPost, likePost, commentOnPost, replyToComment, deletePost, deleteComment, deleteReply, editPost } from '../services/communityService';
import { formatDistanceToNow } from "date-fns";
import { IoSendSharp, IoImageOutline, IoVideocamOutline, IoCloseCircle, IoCopyOutline } from "react-icons/io5";

import { FaHeart, FaRegHeart, FaRegCommentDots, FaShare, FaTrash, FaEllipsisV, FaPeopleCarry, FaUsers } from "react-icons/fa";
import { 
  WhatsappShareButton, WhatsappIcon,
  LinkedinShareButton, LinkedinIcon,
  TwitterShareButton, XIcon,
  FacebookShareButton, FacebookIcon
} from "react-share";

const Community = () => {
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commentTexts, setCommentTexts] = useState({});
  const [replyTexts, setReplyTexts] = useState({});
  const [editPostId, setEditPostId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [openComments, setOpenComments] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openShareId, setOpenShareId] = useState(null);
  const [showReplyInput, setShowReplyInput] = useState({});

  const getInitial = (user) => {
    const str = user?.username || user?.name || "U";
    return str.charAt(0).toUpperCase();
  };

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentUserId = currentUser._id || currentUser.id || null;

  // Fetch posts
  const fetchPosts = async () => {
    try {
      const data = await getPosts();
      setPosts(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPosts();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchPosts, 10000);
    return () => clearInterval(interval);
  }, []);

  // Convert file to Base64 (if not using Cloudinary directly)
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // Create Post
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content && !mediaFile) return;
    setLoading(true);

    try {
      let mediaBase64;
      if (mediaFile) {
        mediaBase64 = await toBase64(mediaFile);
      }

      const data = await createPost({
        content,
        mediaBase64,
      });

      setPosts([data, ...posts]); // Optimistic update
      setContent("");
      setMediaFile(null);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to create post");
    } finally {
      setLoading(false);
    }
  };

  // Like Post
  const handleLike = async (postId) => {
    try {
      const data = await likePost(postId);
      setPosts((prev) =>
        prev.map((p) => (p._id === postId ? data : p))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleComment = async (postId) => {
    const text = (commentTexts[postId] || "").trim();
    if (!text) return;
  
    // Create optimistic comment
    const newComment = {
      _id: Date.now().toString(), // temp id
      text,
      user: currentUser,
      replies: []
    };
  
    // Update UI instantly
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId ? { ...p, comments: [...p.comments, newComment] } : p
      )
    );
  
    // Clear input
    setCommentTexts({ ...commentTexts, [postId]: "" });
  
    try {
      await commentOnPost(postId, { text });
      fetchPosts(); // re-sync with backend
    } catch (err) {
      console.error(err);
    }
  };

 // Add Reply
const handleReply = async (postId, commentId) => {
  const text = (replyTexts[commentId] || "").trim();
  if (!text) return;

  const newReply = {
    _id: Date.now().toString(),
    text,
    user: currentUser
  };

  // Optimistic update
  setPosts((prev) =>
    prev.map((p) =>
      p._id === postId
        ? {
            ...p,
            comments: p.comments.map((c) =>
              c._id === commentId
                ? { ...c, replies: [...(c.replies || []), newReply] }
                : c
            )
          }
        : p
    )
  );

  setReplyTexts({ ...replyTexts, [commentId]: "" });

  try {
    await replyToComment(postId, commentId, { text });
    fetchPosts(); // sync real data
  } catch (err) {
    console.error(err);
  }
};

  // Delete Post
  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await deletePost(postId);
      setPosts((prev) => prev.filter((p) => p._id !== postId));
    } catch (err) {
      console.error(err);
    }
  };

  // Delete Reply
  const handleDeleteReply = async (postId, commentId, replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;
    
    try {
      await deleteReply(postId, commentId, replyId);
      fetchPosts();
    } catch (err) {
      console.error('Error deleting reply:', err.response?.data || err.message);
      alert(err.response?.data?.message || 'Failed to delete reply');
    }
  };

  // Delete Comment
  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await deleteComment(postId, commentId);
      fetchPosts();
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6]" style={{
      backgroundImage: `
        radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
        radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
        radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
      `
    }}>
       {/* Top Decoration */}
       <div className="h-[40rem] bg-gradient-to-r from-primary-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      <div className="container-responsive relative z-10 pt-20 px-4 sm:px-6 lg:px-8 pb-12 items-center justify-center flex flex-col">
          {/* Header Section */}
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-md border border-white/30 text-white rounded-full text-sm font-medium mb-6 shadow-lg">
              <FaUsers className="w-4 h-4 mr-2" />
              Community Forum
            </div>
          <h1 className="heading-primary mb-6 text-white drop-shadow-sm">
              Connect & <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">Share Ideas</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Join the conversation with like-minded professionals, ask questions, and share your journey.
            </p>
          </div>

          <div className="w-full max-w-2xl mx-auto space-y-8">
            {/* Create Post Card */}
             <div className="bg-white/90 backdrop-blur-md rounded-3xl shadow-xl p-6 border border-white/50 relative overflow-hidden transition-all duration-300 hover:shadow-2xl">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                <form onSubmit={handleSubmit}>
                    <div className="flex items-start space-x-4">
                         <div className="flex-shrink-0">
                            {currentUser.avatar ? (
                                <img src={currentUser.avatar} alt="Me" className="w-12 h-12 rounded-full ring-2 ring-white shadow-md object-cover" />
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg ring-2 ring-white shadow-md">
                                    {getInitial(currentUser)}
                                </div>
                            )}
                         </div>
                         <div className="flex-1">
                             <textarea
                                className="w-full bg-gray-50 border-0 rounded-xl p-4 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-100 focus:bg-white transition-all resize-none min-h-[100px]"
                                placeholder={`What's on your mind, ${currentUser.username || 'user'}?`}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                             />
                             
                             {/* Media Preview */}
                             {mediaFile && (
                                <div className="mt-4 relative group rounded-xl overflow-hidden border border-gray-200 shadow-sm inline-block max-w-sm">
                                    {mediaFile.type.startsWith('image/') ? (
                                        <img 
                                            src={URL.createObjectURL(mediaFile)} 
                                            alt="Preview" 
                                            className="w-full max-h-60 object-cover"
                                        />
                                    ) : mediaFile.type.startsWith('video/') ? (
                                        <video 
                                            src={URL.createObjectURL(mediaFile)}
                                            className="w-full max-h-60 object-cover"
                                            controls
                                        />
                                    ) : null}
                                    <button
                                        type="button"
                                        onClick={() => setMediaFile(null)}
                                        className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-colors backdrop-blur-sm"
                                    >
                                        <IoCloseCircle className="w-5 h-5" />
                                    </button>
                                </div>
                             )}

                             <div className="flex items-center justify-between mt-4 border-t border-gray-100 pt-4">
                                 <div className="flex space-x-2">
                                     <label className="flex items-center px-4 py-2 rounded-lg text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors cursor-pointer text-sm font-semibold">
                                         <input
                                             type="file"
                                             className="hidden"
                                             accept="image/*"
                                             onChange={(e) => setMediaFile(e.target.files[0])}
                                         />
                                         <IoImageOutline className="w-5 h-5 mr-2" />
                                         Photo
                                     </label>
                                     
                                 </div>
                                 <button
                                    type="submit"
                                    disabled={loading || (!content && !mediaFile)}
                                    className={`px-6 py-2 rounded-xl text-white font-bold shadow-md transition-all ${
                                        loading || (!content && !mediaFile)
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-lg hover:scale-105'
                                    }`}
                                 >
                                     {loading ? (
                                        <div className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Posting...
                                        </div>
                                     ) : "Post"}
                                 </button>
                             </div>
                         </div>
                    </div>
                </form>
             </div>

             {/* Posts Feed */}
             <div className="space-y-6">
                {posts.map((post) => (
                    <div key={post._id} className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg hover:shadow-xl transition-shadow relative">
                        {/* Author Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                {post.author?.avatar ? (
                                    <img src={post.author.avatar} alt="Author" className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                                        {getInitial(post.author)}
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-bold text-gray-900">{post.author?.username || post.author?.name || "User"}</h3>
                                    <p className="text-xs text-gray-500 font-medium">{formatDistanceToNow(new Date(post.createdAt))} ago</p>
                                </div>
                            </div>

                            {post.author?._id === currentUserId && (
                                <div className="relative">
                                    <button 
                                        onClick={() => setOpenMenuId(openMenuId === post._id ? null : post._id)}
                                        className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <FaEllipsisV />
                                    </button>
                                    {openMenuId === post._id && (
                                        <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                            <button 
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                onClick={() => {
                                                    setEditPostId(post._id);
                                                    setEditContent(post.content);
                                                    setOpenMenuId(null);
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                onClick={() => {
                                                    setOpenMenuId(null);
                                                    handleDelete(post._id);
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Post Content */}
                        <div className="mb-4 text-gray-800 leading-relaxed font-normal">
                            {editPostId === post._id ? (
                                <form onSubmit={async (e) => {
                                    e.preventDefault();
                                    const updatedPost = { ...post, content: editContent, updatedAt: new Date().toISOString() };
                                    setPosts((prev) => prev.map((p) => (p._id === post._id ? updatedPost : p)));
                                    setEditPostId(null);
                                    setEditContent("");
                                    try {
                                        await editPost(post._id, { content: editContent });
                                        fetchPosts(); 
                                    } catch (err) { console.error(err); }
                                }}>
                                    <textarea
                                        className="w-full p-3 border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        rows="3"
                                        value={editContent}
                                        onChange={(e) => setEditContent(e.target.value)}
                                    />
                                    <div className="flex space-x-2 mt-2 justify-end">
                                        <button type="button" onClick={() => setEditPostId(null)} className="px-4 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
                                        <button type="submit" className="px-4 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm">Save</button>
                                    </div>
                                </form>
                            ) : (
                                <p className="whitespace-pre-wrap">{post.content}</p>
                            )}
                        </div>

                        {/* Media Display */}
                        {post.mediaUrl && (
                            <div className="mb-5 rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                                {post.mediaType === "image" ? (
                                    <img src={post.mediaUrl} alt="Post media" className="w-full max-h-[500px] object-cover" />
                                ) : (
                                    <video controls className="w-full max-h-[500px] bg-black">
                                        <source src={post.mediaUrl} />
                                    </video>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-2">
                            <div className="flex space-x-6">
                                <button
                                    onClick={() => handleLike(post._id)}
                                    className={`flex items-center space-x-2 text-sm font-semibold transition-colors ${
                                        post.likes?.includes(currentUserId) ? 'text-pink-600' : 'text-gray-500 hover:text-pink-500'
                                    }`}
                                >
                                    {post.likes?.includes(currentUserId) ? <FaHeart className="w-5 h-5" /> : <FaRegHeart className="w-5 h-5" />}
                                    <span>{post.likes?.length || 0}</span>
                                </button>

                                <button
                                    onClick={() => setOpenComments((prev) => ({ ...prev, [post._id]: !prev[post._id] }))}
                                    className={`flex items-center space-x-2 text-sm font-semibold transition-colors ${
                                        openComments[post._id] ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'
                                    }`}
                                >
                                    <FaRegCommentDots className="w-5 h-5" />
                                    <span>{post.comments.length}</span>
                                </button>
                            </div>



                             <div className="relative">
                                <button
                                    onClick={() => setOpenShareId(openShareId === post._id ? null : post._id)}
                                    className={`flex items-center space-x-2 text-sm font-semibold transition-colors ${
                                        openShareId === post._id ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'
                                    }`}
                                >
                                    <FaShare className="w-5 h-5" />
                                    <span>Share</span>
                                </button>
                                
                                {openShareId === post._id && (
                                    <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-20 animate-in fade-in zoom-in-95 duration-200">
                                        <div className="grid grid-cols-4 gap-2 mb-2 p-2">
                                            <WhatsappShareButton url={`${window.location.origin}/community#${post._id}`} title={post.content} separator=" - ">
                                                <WhatsappIcon size={32} round />
                                            </WhatsappShareButton>
                                            <LinkedinShareButton url={`${window.location.origin}/community#${post._id}`} title={post.content} summary={post.content} source="SkillBridgeAI">
                                                <LinkedinIcon size={32} round />
                                            </LinkedinShareButton>
                                            <TwitterShareButton url={`${window.location.origin}/community#${post._id}`} title={post.content}>
                                                <XIcon size={32} round />
                                            </TwitterShareButton>
                                            <FacebookShareButton url={`${window.location.origin}/community#${post._id}`} quote={post.content}>
                                                <FacebookIcon size={32} round />
                                            </FacebookShareButton>
                                        </div>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${window.location.origin}/community#${post._id}`);
                                                setOpenShareId(null);
                                                alert("Link copied to clipboard!");
                                            }}
                                            className="w-full flex items-center justify-center space-x-2 p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors border-t border-gray-50 bg-gray-50/50"
                                        >
                                            <IoCopyOutline />
                                            <span>Copy Link</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Comments Section */}
                        {openComments[post._id] && (
                            <div className="mt-5 pt-5 border-t border-gray-100 animate-in slide-in-from-top-2">
                                <form
                                    onSubmit={(e) => { e.preventDefault(); handleComment(post._id); }}
                                    className="flex items-center space-x-3 mb-6"
                                >
                                    <input
                                        type="text"
                                        className="flex-1 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-full focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5 px-4"
                                        placeholder="Write a comment..."
                                        value={commentTexts[post._id] || ""}
                                        onChange={(e) => setCommentTexts({ ...commentTexts, [post._id]: e.target.value })}
                                    />
                                    <button
                                        type="submit"
                                        disabled={!commentTexts[post._id]}
                                        className="p-2.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
                                    >
                                        <IoSendSharp className="w-4 h-4 pl-0.5" />
                                    </button>
                                </form>

                                <div className="space-y-4">
                                    {post.comments.map((comment) => (
                                        <div key={comment._id} className="flex space-x-3">
                                            <div className="flex-shrink-0 mt-1">
                                                {comment.user?.avatar ? (
                                                    <img src={comment.user.avatar} alt="User" className="w-8 h-8 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">
                                                        {getInitial(comment.user)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="bg-gray-50 rounded-2xl p-3 px-4 inline-block min-w-[200px]">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="font-bold text-sm text-gray-900">{comment.user?.username || comment.user?.name}</span>
                                                        {(comment.user?._id === currentUserId || comment.user?.id === currentUserId) && (
                                                            <button onClick={() => handleDeleteComment(post._id, comment._id)} className="text-gray-400 hover:text-red-500 ml-2">
                                                                <FaTrash size={10} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-700">{comment.text}</p>
                                                </div>
                                                
                                                <div className="flex items-center space-x-4 mt-1 ml-2">
                                                    <button 
                                                        onClick={() => setShowReplyInput(prev => ({ ...prev, [comment._id]: !prev[comment._id] }))}
                                                        className="text-xs font-semibold text-gray-500 hover:text-indigo-600"
                                                    >
                                                        Reply
                                                    </button>
                                                </div>

                                                {/* Reply Input */}
                                                {showReplyInput[comment._id] && (
                                                    <form
                                                        onSubmit={(e) => { e.preventDefault(); handleReply(post._id, comment._id); setShowReplyInput(prev => ({ ...prev, [comment._id]: false })); }}
                                                        className="mt-2 flex items-center space-x-2"
                                                    >
                                                        <input
                                                            type="text"
                                                            className="flex-1 bg-white border border-gray-200 text-xs rounded-full p-2 px-3"
                                                            placeholder="Reply..."
                                                            value={replyTexts[comment._id] || ""}
                                                            onChange={(e) => setReplyTexts({ ...replyTexts, [comment._id]: e.target.value })}
                                                            autoFocus
                                                        />
                                                    </form>
                                                )}

                                                {/* Replies */}
                                                {comment.replies?.length > 0 && (
                                                    <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-100">
                                                        {comment.replies.map(reply => (
                                                            <div key={reply._id} className="flex space-x-2 pl-2">
                                                                 <div className="flex-shrink-0 mt-0.5">
                                                                    {reply.user?.avatar ? (
                                                                        <img src={reply.user.avatar} alt="User" className="w-6 h-6 rounded-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                                                                            {getInitial(reply.user)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="bg-gray-50 rounded-xl p-2 px-3">
                                                                         <div className="flex items-center justify-between">
                                                                             <span className="font-bold text-xs text-gray-900">{reply.user?.username || reply.user?.name}</span>
                                                                             {(reply.user?._id === currentUserId || reply.user?.id === currentUserId) && (
                                                                                <button onClick={() => handleDeleteReply(post._id, comment._id, reply._id)} className="text-gray-400 hover:text-red-500 ml-2">
                                                                                    <FaTrash size={9} />
                                                                                </button>
                                                                            )}
                                                                         </div>
                                                                         <p className="text-xs text-gray-700 mt-0.5">{reply.text}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
             </div>
          </div>
      </div>
    </div>
  );
};

export default Community;
